import React, { useState, useEffect } from 'react';
import {
  FiInfo,
  FiCalendar,
  FiUser,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiX,
  FiCheck,
  FiSend
} from 'react-icons/fi';
import { FaMoneyBillWave } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../libs/axios';
import { BiRupee } from 'react-icons/bi';
import { formatDate } from '../../activities/edit';
import EntityLogs from '../../../utils/logs.utils';
import { LuActivity } from 'react-icons/lu';
import TimeInput24h from '../../../utils/time-input-24h';
import { useAuth } from '../../../provider/auth.provider';

const formatDateOnly = (dateString: string) => {
  return new Date(dateString + 'T00:00:00').toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  association_type: string;
  profile_picture: string;
  employee_code?: string;
  designation?: {
    id: number;
    name?: string;
  } | null;
  user_details?: {
    bank_name?: string | null;
    bank_account_number?: string | null;
    ifsc_code?: string | null;
    payment_qr_code?: string | null;
  } | null;
}

interface Advance {
  id: number;
  employee_id: number;
  accounting_name: string | null;
  payment_qr_code?: string | null;
  use_existing_qr?: boolean | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  ifsc_code?: string | null;
  request_amount: number;
  advance_reason: string;
  request_remarks: string | null;
  status: string;
  approved_by: number | null;
  approved_date: string | null;
  approved_amount?: number | null;
  approval_remarks?: string | null;
  rejection_reason: string | null;
  rejected_at: string | null;
  rejected_by: number | null;
  transferred_by: number | null;
  transferred_amount?: number | null;
  transfer_mode?: string | null;
  transfer_reference?: string | null;
  transfer_remarks?: string | null;
  transferred_date: string | null;
  transferred_time?: string | null;
  transfer_time?: string | null;
  created_at: string;
  updated_at: string;
  employee: Employee;
  approver: Employee | null;
  rejector: Employee | null;
  transferrer: Employee | null;
  completer?: Employee | null;
  completed_at?: string | null;
  completion_remarks?: string | null;
  metrics?: {
    currentMonthRequestedAmountTotal: number;
    currentMonthApprovedAmountTotal: number;
    currentMonthTransferredAmountTotal: number;
    currentMonthFieldWorkCompletedTotal: number;
    currentMonthFieldWorkAssignedTotal: number;
  };
}

interface TransferFormData {
  transferred_amount: number;
  transfer_mode: string;
  transfer_reference: string;
  transfer_remarks: string;
  transfer_date: string;
  transfer_time: string;
}

const AdvanceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const canApproveAdvance = hasPermission('advance_claim', 'approve');
  const canViewPaymentInfo = hasPermission('advance_claim', 'view_payment_info');
  const [advance, setAdvance] = useState<Advance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [showApprovalDrawer, setShowApprovalDrawer] = useState<boolean>(false);
  const [showRejectDrawer, setShowRejectDrawer] = useState<boolean>(false);
  const [showTransferDrawer, setShowTransferDrawer] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [approvedAmount, setApprovedAmount] = useState<number>(0);
  const [approvalRemarks, setApprovalRemarks] = useState<string>('');
  const [transferData, setTransferData] = useState<TransferFormData>({
    transferred_amount: 0,
    transfer_mode: 'bank_transfer',
    transfer_reference: '',
    transfer_remarks: '',
    transfer_date: '',
    transfer_time: ''
  });

  const [errors, setErrors] = useState<any>({});
  const [touched, setTouched] = useState<any>({});
  const approvedOrRequestedAmount = advance?.approved_amount ?? advance?.request_amount ?? 0;
  const actualTransferredAmount = advance?.transferred_amount ?? approvedOrRequestedAmount;
  const isRequestor = !!advance && advance.employee_id === user?.id;
  // "completed" is the stage after "transferred": the money has moved in both.
  const hasBeenTransferred = advance?.status === 'transferred' || advance?.status === 'completed';
  const [showCompleteModal, setShowCompleteModal] = useState<boolean>(false);
  const [completeData, setCompleteData] = useState({ transfer_reference: '', completion_remarks: '' });
  const [completeError, setCompleteError] = useState<string>('');

  const tabs = [
    { id: 'details', label: 'Advance Details', icon: FiInfo },
    { id: 'workflow', label: 'Workflow', icon: FiSend },
    { id: 'logs', label: 'Logs', icon: LuActivity },
  ];

  useEffect(() => {
    fetchAdvanceData();
  }, [id]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !actionLoading) {
        if (showApprovalDrawer) setShowApprovalDrawer(false);
        if (showRejectDrawer) setShowRejectDrawer(false);
        if (showTransferDrawer) setShowTransferDrawer(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showApprovalDrawer, showRejectDrawer, showTransferDrawer, actionLoading]);

  const fetchAdvanceData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/claims/advance/${id}`);
      setAdvance(response.data.data);

      if (response.data.data) {
        setApprovedAmount(response.data.data.approved_amount ?? response.data.data.request_amount);
        setTransferData({
          transferred_amount: response.data.data.transferred_amount ?? response.data.data.approved_amount ?? response.data.data.request_amount,
          transfer_mode: response.data.data.transfer_mode || 'bank_transfer',
          transfer_reference: response.data.data.transfer_reference || '',
          transfer_remarks: response.data.data.transfer_remarks || '',
          transfer_date: response.data.data.transferred_date || '',
          transfer_time: response.data.data.transferred_time || response.data.data.transfer_time || ''
        });
      }
    } catch (error) {
      console.error('Error fetching advance:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateApprovalForm = (): boolean => {
    const newErrors: any = {};

    if (!approvedAmount || approvedAmount <= 0) {
      newErrors.approvedAmount = 'Approved amount must be greater than 0';
    } else if (approvedAmount > (advance?.request_amount || 0)) {
      newErrors.approvedAmount = 'Approved amount cannot exceed requested amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateRejectForm = (): boolean => {
    const newErrors: any = {};

    if (!rejectionReason.trim()) {
      newErrors.rejectionReason = 'Rejection reason is required';
    } else if (rejectionReason.trim().length < 10) {
      newErrors.rejectionReason = 'Rejection reason must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTransferForm = (): boolean => {
    const newErrors: any = {};

    if (transferData.transferred_amount < 0) {
      newErrors.transferred_amount = 'Transfer amount cannot be negative';
    } else if (transferData.transferred_amount > approvedOrRequestedAmount) {
      newErrors.transferred_amount = 'Transfer amount cannot exceed approved amount';
    }

    // The reference number is optional here; it becomes mandatory when the
    // advance is completed.

    if (!transferData.transfer_date) {
      newErrors.transfer_date = 'Transfer date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApprove = async () => {
    if (!canApproveAdvance) return;
    if (!validateApprovalForm()) return;

    try {
      setActionLoading(true);
      await axiosInstance.put('/claims/advance/approve-reject', {
        id: Number(id),
        status: 'approved',
        approved_amount: approvedAmount,
        approval_remarks: approvalRemarks
      });
      await fetchAdvanceData();
      setShowApprovalDrawer(false);
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error approving advance:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!canApproveAdvance) return;
    if (!validateRejectForm()) return;

    try {
      setActionLoading(true);
      await axiosInstance.put('/claims/advance/approve-reject', {
        id: Number(id),
        status: 'rejected',
        rejection_reason: rejectionReason
      });
      await fetchAdvanceData();
      setShowRejectDrawer(false);
      setRejectionReason('');
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error rejecting advance:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!canApproveAdvance) return;
    if (!validateTransferForm()) return;

    try {
      setActionLoading(true);
      const payload: Record<string, unknown> = {
        id: Number(id),
        transferred_amount: transferData.transferred_amount,
        transfer_mode: transferData.transfer_mode,
        transfer_reference: transferData.transfer_reference,
        transfer_remarks: transferData.transfer_remarks,
        transfer_date: transferData.transfer_date,
      };

      if (transferData.transfer_time) {
        payload.transfer_time = transferData.transfer_time;
      }

      await axiosInstance.put('/claims/advance/transfer', payload);
      await fetchAdvanceData();
      setShowTransferDrawer(false);
      setErrors({});
      setTouched({});
    } catch (error) {
      console.error('Error transferring advance:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const openCompleteModal = () => {
    setCompleteData({ transfer_reference: advance?.transfer_reference || '', completion_remarks: '' });
    setCompleteError('');
    setShowCompleteModal(true);
  };

  const handleComplete = async () => {
    if (!canApproveAdvance) return;
    if (!completeData.transfer_reference.trim()) {
      setCompleteError('Reference number is required');
      return;
    }

    try {
      setActionLoading(true);
      setCompleteError('');
      await axiosInstance.put('/claims/advance/complete', {
        id: Number(id),
        transfer_reference: completeData.transfer_reference.trim(),
        ...(completeData.completion_remarks.trim() ? { completion_remarks: completeData.completion_remarks.trim() } : {}),
      });
      await fetchAdvanceData();
      setShowCompleteModal(false);
    } catch (error: any) {
      setCompleteError(error?.response?.data?.message || 'Failed to complete advance');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'requested': { icon: FiClock, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', label: 'Requested' },
      'approved': { icon: FiCheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800', label: 'Approved' },
      'transferred': { icon: FaMoneyBillWave, bgColor: 'bg-blue-100', textColor: 'text-blue-800', label: 'Transferred' },
      'completed': { icon: FiCheckCircle, bgColor: 'bg-teal-100', textColor: 'text-teal-800', label: 'Completed' },
      'rejected': { icon: FiAlertCircle, bgColor: 'bg-red-100', textColor: 'text-red-800', label: 'Rejected' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.requested;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const getReasonLabel = (reason: string) => {
    const reasons: Record<string, string> = {
      'site_work': 'Site Work',
      'travel': 'Travel',
      'material': 'Material Purchase',
      'other': 'Other'
    };
    return reasons[reason] || reason;
  };

  const formatMonthYear = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const getFullName = (employee?: Employee | null) => {
    if (!employee) return 'N/A';
    return `${employee.first_name} ${employee.last_name}`;
  };

  const getAssociationBadge = (type: string) => {
    return type === 'inhouse' ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
        Inhouse
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
        Vendor
      </span>
    );
  };

  const renderDetailsTab = () => (
    <div className="space-y-6">
      {/* Monthly Performance Summary Header */}
      <div className="bg-white rounded-t shadow-sm border border-gray-200 border-b-0 px-4 py-3">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <FiInfo className="text-blue-600" />
          Monthly Performance Summary (Current Month)
        </h3>
      </div>
      {/* Metrics Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 transition-all hover:shadow-md border-t-4 border-t-yellow-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Current Month Requested Amount Total</span>
            <FiClock className="text-yellow-500 w-4 h-4" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-gray-900">
              {formatCurrency(advance?.metrics?.currentMonthRequestedAmountTotal || 0)}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Total requests this month</p>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 transition-all hover:shadow-md border-t-4 border-t-green-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Current Month Approved Amount Total</span>
            <FiCheckCircle className="text-green-500 w-4 h-4" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-gray-900">
              {formatCurrency(advance?.metrics?.currentMonthApprovedAmountTotal || 0)}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Total approved this month</p>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 transition-all hover:shadow-md border-t-4 border-t-blue-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Current Month Transferred Amount Total</span>
            <FaMoneyBillWave className="text-blue-500 w-4 h-4" />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-gray-900">
              {formatCurrency(advance?.metrics?.currentMonthTransferredAmountTotal || 0)}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Total transferred this month</p>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 transition-all hover:shadow-md border-t-4 border-t-purple-500">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Current Month Field Work Completed Total</span>
            <LuActivity className="text-purple-500 w-4 h-4" />
          </div>
          <div className="flex items-center mt-2">
            <div
              className="flex-1 flex flex-col"
              title="Sites whose field work the engineer completed this month, taken from the workflow history — a site that has since moved on to TA review, reporting or Report Sent To Client still counts here."
            >
              <span className="text-xl font-bold text-gray-900">
                {advance?.metrics?.currentMonthFieldWorkCompletedTotal || 0}
              </span>
              <span className="text-[10px] text-gray-500 font-medium uppercase tracking-tight">Completed</span>
            </div>
            <div className="h-8 w-[1px] bg-gray-200 mx-1"></div>
            <div
              className="flex-1 flex flex-col pl-2"
              title="Sites assigned to the engineer this month that are still waiting on them — workflow status is Engineer Assigned."
            >
              <span className="text-xl font-bold text-gray-700">
                {advance?.metrics?.currentMonthFieldWorkAssignedTotal || 0}
              </span>
              <span className="text-[10px] text-gray-500 font-medium uppercase tracking-tight">
                Assigned · Pending
              </span>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 mt-1">Performance this month</p>
        </div>
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500">Employee Name</label>
            <p className="text-sm text-gray-900 mt-1 font-semibold">{getFullName(advance?.employee)}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Employee Code</label>
            <p className="text-sm text-gray-900 mt-1">{advance?.employee?.employee_code || '-'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Designation</label>
            <p className="text-sm text-gray-900 mt-1">{advance?.employee?.designation?.name || '-'}</p>
          </div>
          {/* <div>
            <label className="text-xs font-medium text-gray-500">Accounting Name</label>
            <p className="text-sm text-gray-900 mt-1">{advance?.accounting_name || 'N/A'}</p>
          </div> */}
          <div>
            <label className="text-xs font-medium text-gray-500">Employee Type</label>
            <div className="mt-1">
              {advance?.employee.association_type ? getAssociationBadge(advance.employee.association_type) : 'N/A'}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Advance Reason</label>
            <p className="text-sm text-gray-900 mt-1">
              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${advance?.advance_reason === 'site_work' ? 'bg-blue-100 text-blue-800' :
                advance?.advance_reason === 'travel' ? 'bg-green-100 text-green-800' :
                  advance?.advance_reason === 'material' ? 'bg-purple-100 text-purple-800' :
                    'bg-orange-100 text-orange-800'
                }`}>
                {getReasonLabel(advance?.advance_reason || '')}
              </span>
            </p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Request Amount</label>
            <p className="text-sm mt-1 font-bold text-blue-900">{formatCurrency(advance?.request_amount)}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Requested At</label>
            <p className="text-sm text-gray-900 mt-1">{advance?.created_at ? formatDate(advance.created_at) : 'N/A'}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Status</label>
            <div className="mt-1">{getStatusBadge(advance?.status || 'requested')}</div>
          </div>
          {advance?.request_remarks && (
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500">Request Remarks</label>
              <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded">{advance.request_remarks}</p>
            </div>
          )}
        </div>
      </div>

      {/* Approval Information */}
      {(advance?.status === 'approved' || hasBeenTransferred) && advance?.approver && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Approval Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Approved By</label>
              <p className="text-sm text-gray-900 mt-1 font-semibold">{getFullName(advance?.approver)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Approver Type</label>
              <div className="mt-1">
                {advance?.approver.association_type ? getAssociationBadge(advance.approver.association_type) : 'N/A'}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Approved At</label>
              <p className="text-sm text-gray-900 mt-1">{advance?.approved_date ? formatDate(advance.approved_date) : 'N/A'}</p>
            </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Approved Amount</label>
                <p className="text-sm mt-1 font-bold text-green-900">{formatCurrency(approvedOrRequestedAmount)}</p>
              </div>
            {advance?.approval_remarks && (
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-gray-500">Approval Remarks</label>
                <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded">{advance.approval_remarks}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transfer Information */}
      {hasBeenTransferred && advance?.transferrer && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Transfer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Transferred By</label>
              <p className="text-sm text-gray-900 mt-1 font-semibold">{getFullName(advance?.transferrer)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Transferrer Type</label>
              <div className="mt-1">
                {advance?.transferrer.association_type ? getAssociationBadge(advance.transferrer.association_type) : 'N/A'}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Transfer Date</label>
              <p className="text-sm text-gray-900 mt-1">{advance?.transferred_date ? formatDateOnly(advance.transferred_date) : 'N/A'}</p>
            </div>
            {(advance?.transferred_time || advance?.transfer_time) && (
              <div>
                <label className="text-xs font-medium text-gray-500">Transfer Time</label>
                <p className="text-sm text-gray-900 mt-1">{advance?.transferred_time || advance?.transfer_time}</p>
              </div>
            )}
              <div>
                <label className="text-xs font-medium text-gray-500">Transferred Amount</label>
                <p className="text-sm mt-1 font-bold text-blue-900">{formatCurrency(actualTransferredAmount)}</p>
              </div>
              {advance?.transfer_mode && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Transfer Mode</label>
                  <p className="text-sm text-gray-900 mt-1 capitalize">{advance.transfer_mode.replace(/_/g, ' ')}</p>
                </div>
              )}
              {advance?.transfer_reference && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Transfer Reference</label>
                  <p className="text-sm text-gray-900 mt-1 break-all">{advance.transfer_reference}</p>
                </div>
              )}
              {advance?.transfer_remarks && (
                <div className="md:col-span-2">
                  <label className="text-xs font-medium text-gray-500">Transfer Remarks</label>
                  <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded">{advance.transfer_remarks}</p>
                </div>
              )}
              {advance?.status === 'completed' && (
                <>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Completed By</label>
                    <p className="text-sm text-gray-900 mt-1 font-semibold">{advance.completer ? getFullName(advance.completer) : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Completed On</label>
                    <p className="text-sm text-gray-900 mt-1">{advance.completed_at ? formatDateOnly(advance.completed_at) : 'N/A'}</p>
                  </div>
                  {advance.completion_remarks && (
                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-gray-500">Completion Remarks</label>
                      <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-3 rounded">{advance.completion_remarks}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

      {/* Rejection Information */}
      {advance?.status === 'rejected' && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Rejection Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Rejected By</label>
              <p className="text-sm text-gray-900 mt-1 font-semibold">{getFullName(advance?.rejector)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Rejector Type</label>
              <div className="mt-1">
                {advance?.rejector?.association_type ? getAssociationBadge(advance.rejector.association_type) : 'N/A'}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Rejected At</label>
              <p className="text-sm text-gray-900 mt-1">{advance?.rejected_at ? formatDate(advance.rejected_at) : 'N/A'}</p>
            </div>
            {advance?.rejection_reason && (
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-gray-500">Rejection Reason</label>
                <p className="text-sm text-red-900 mt-1 bg-red-50 p-3 rounded">{advance.rejection_reason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bank & Payment Info — full details for view_payment_info; QR only for requestor */}
      {(canViewPaymentInfo || isRequestor) && (
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Bank &amp; Payment Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {canViewPaymentInfo && (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-500">Bank Name</label>
                  <p className="text-sm text-gray-900 mt-1">{advance?.bank_name || advance?.employee?.user_details?.bank_name || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Account Number</label>
                  <p className="text-sm text-gray-900 mt-1 font-mono tracking-wide">{advance?.bank_account_number || advance?.employee?.user_details?.bank_account_number || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">IFSC Code</label>
                  <p className="text-sm text-gray-900 mt-1 font-mono tracking-wide">{advance?.ifsc_code || advance?.employee?.user_details?.ifsc_code || '-'}</p>
                </div>
              </>
            )}
            {(advance?.payment_qr_code || advance?.employee?.user_details?.payment_qr_code) ? (
              <div>
                <label className="text-xs font-medium text-gray-500">
                  Payment QR Code
                  {advance?.payment_qr_code && (
                    <span className="ml-2 text-blue-600 font-normal">
                      ({advance.use_existing_qr ? 'From profile' : 'Uploaded with request'})
                    </span>
                  )}
                </label>
                <div className="mt-2">
                  <img
                    src={advance?.payment_qr_code || advance?.employee?.user_details?.payment_qr_code || ''}
                    alt="Payment QR Code"
                    className="w-52 h-52 object-contain border border-gray-200 rounded"
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">No QR code attached</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderWorkflowTab = () => {
    const isApproved = advance?.status === 'approved' || hasBeenTransferred;
    const isRejected = advance?.status === 'rejected';
    const isTransferred = hasBeenTransferred;
    const isCompleted = advance?.status === 'completed';

    return (
      <div className="space-y-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-6">Workflow Timeline</h3>

          {/* Timeline */}
          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute left-4 top-8 bottom-8 w-0.5 bg-gray-200"></div>

            {/* Step 1: Request Submitted */}
            <div className="relative flex items-start mb-8">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${advance ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                } z-10 shadow-md`}>
                <FiSend className="w-4 h-4" />
              </div>
              <div className="ml-6 flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">Request Submitted</h4>
                  <span className="text-xs text-gray-500">
                    {advance?.created_at ? formatDate(advance.created_at) : 'Pending'}
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Employee:</span> {getFullName(advance?.employee)}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Employee Code:</span> {advance?.employee?.employee_code || '-'}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Employee Type:</span> {advance?.employee.association_type ? (
                      <span className="capitalize">{advance.employee.association_type}</span>
                    ) : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Amount:</span> {formatCurrency(advance?.request_amount)}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Reason:</span> {getReasonLabel(advance?.advance_reason || '')}
                  </p>
                  {advance?.accounting_name && (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Accounting Name:</span> {advance.accounting_name}
                    </p>
                  )}
                  {advance?.request_remarks && (
                    <p className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded mt-2">
                      "{advance.request_remarks}"
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: PM Review/Approval */}
            <div className="relative flex items-start mb-8">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${isApproved ? 'bg-green-600 text-white' :
                isRejected ? 'bg-red-600 text-white' :
                  'bg-gray-300 text-gray-600'
                } z-10 shadow-md`}>
                {isApproved ? <FiCheck className="w-4 h-4" /> :
                  isRejected ? <FiX className="w-4 h-4" /> :
                    <FiClock className="w-4 h-4" />}
              </div>
              <div className="ml-6 flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {isRejected ? 'Request Rejected' : 'PM Review & Approval'}
                  </h4>
                  <span className="text-xs text-gray-500">
                    {isApproved && advance?.approved_date
                      ? formatDate(advance.approved_date)
                      : isRejected
                        ? (advance?.rejected_at ? formatDate(advance.rejected_at) : 'Pending')
                        : 'Pending'}
                  </span>
                </div>
                {isApproved && advance?.approver && (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Approved By:</span> {getFullName(advance?.approver)}
                    </p>
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Approver Type:</span> {advance?.approver.association_type ? (
                        <span className="capitalize">{advance.approver.association_type}</span>
                      ) : 'N/A'}
                    </p>
                    <p className="text-xs text-green-700 font-medium">
                      Status: Approved ✓
                    </p>
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Approved Amount:</span> {formatCurrency(advance?.approved_amount || advance?.request_amount)}
                    </p>
                    {advance?.approval_remarks && (
                      <p className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded mt-2">
                        "{advance.approval_remarks}"
                      </p>
                    )}
                  </div>
                )}
                {isRejected && (
                  <div className="mt-2 space-y-2">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Rejected By:</span> {getFullName(advance?.rejector)}
                    </div>
                    {advance?.rejector?.association_type && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Rejector Type:</span> <span className="capitalize">{advance.rejector.association_type}</span>
                      </div>
                    )}
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">Rejected At:</span> {advance?.rejected_at ? formatDate(advance.rejected_at) : 'Pending'}
                    </div>
                    <p className="text-xs text-red-700 font-medium">
                      Status: Rejected ✗
                    </p>
                    {advance?.rejection_reason && (
                      <p className="text-xs text-red-600 italic bg-red-50 p-2 rounded">
                        Reason: {advance.rejection_reason}
                      </p>
                    )}
                  </div>
                )}
                {!isApproved && !isRejected && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 italic">Awaiting PM approval...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Step 3: Transfer (Only shown if not rejected) */}
            {!isRejected && (
              <div className="relative flex items-start">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${isTransferred ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                  } z-10 shadow-md`}>
                  <FaMoneyBillWave className="w-4 h-4" />
                </div>
                <div className="ml-6 flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">Amount Transfer</h4>
                    <span className="text-xs text-gray-500">
                      {advance?.transferred_date ? formatDateOnly(advance.transferred_date) : 'Pending'}
                      {(advance?.transferred_time || advance?.transfer_time) && (
                        <span> | {advance?.transferred_time || advance?.transfer_time}</span>
                      )}
                    </span>
                  </div>
                  {isTransferred && advance?.transferrer && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Transferred By:</span> {getFullName(advance?.transferrer)}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Transferrer Type:</span> {advance?.transferrer.association_type ? (
                          <span className="capitalize">{advance.transferrer.association_type}</span>
                        ) : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Transferred Amount:</span> {formatCurrency(actualTransferredAmount)}
                      </p>
                      {advance?.transfer_mode && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Transfer Mode:</span> {advance.transfer_mode.replace(/_/g, ' ')}
                        </p>
                      )}
                      {advance?.transfer_reference && (
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Reference:</span> {advance.transfer_reference}
                        </p>
                      )}
                      {advance?.transfer_remarks && (
                        <p className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded mt-2">
                          "{advance.transfer_remarks}"
                        </p>
                      )}
                      <p className="text-xs text-blue-700 font-medium">
                        Status: Transferred ✓
                      </p>
                    </div>
                  )}
                  {!isTransferred && isApproved && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 italic">Awaiting transfer...</p>
                    </div>
                  )}
                  {!isApproved && !isTransferred && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-400 italic">Pending approval before transfer</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Complete (Only shown if not rejected) */}
            {!isRejected && (
              <div className="relative flex items-start">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${isCompleted ? 'bg-teal-600 text-white' : 'bg-gray-300 text-gray-600'
                  } z-10 shadow-md`}>
                  <FiCheckCircle className="w-4 h-4" />
                </div>
                <div className="ml-6 flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">Complete</h4>
                    <span className="text-xs text-gray-500">
                      {advance?.completed_at ? formatDateOnly(advance.completed_at) : 'Pending'}
                    </span>
                  </div>
                  {isCompleted ? (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Completed By:</span> {advance?.completer ? getFullName(advance.completer) : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Reference:</span> {advance?.transfer_reference || 'N/A'}
                      </p>
                      {advance?.completion_remarks && (
                        <p className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded mt-2">
                          "{advance.completion_remarks}"
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500 italic">
                        {isTransferred ? 'Awaiting reference details...' : 'Pending transfer before completion'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Summary Card */}
          <div className={`mt-6 p-4 rounded-lg border-2 ${isTransferred ? 'bg-blue-50 border-blue-200' :
            isApproved ? 'bg-green-50 border-green-200' :
              isRejected ? 'bg-red-50 border-red-200' :
                'bg-yellow-50 border-yellow-200'
            }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">Current Status</p>
                <p className="text-xs text-gray-600 mt-1">
                  {isCompleted ? 'Advance has been transferred and completed with its reference details' :
                    isTransferred ? 'Advance has been transferred to the employee and is awaiting completion' :
                    isApproved ? 'Advance has been approved and is awaiting transfer' :
                      isRejected ? 'Advance request has been rejected' :
                        'Advance request is pending approval'}
                </p>
              </div>
              {getStatusBadge(advance?.status || 'requested')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading advance details...</p>
        </div>
      </div>
    );
  }

  if (!advance) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Advance Not Found</h2>
          <p className="text-gray-600 mb-4">The advance you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/claims/advance')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Advances
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Advance #{advance.id}</h1>
              <p className="text-sm text-gray-600 mt-1">
                Requested By: {getFullName(advance.employee)}
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-2">
                <div className="flex items-center gap-1">
                  <FiCalendar size={12} />
                  Requested: {formatDate(advance.created_at)}
                </div>
                <div className="flex items-center gap-1">
                  <BiRupee size={12} />
                  {formatCurrency(advance.request_amount)}
                </div>
                {advance.status === 'rejected' && advance.rejected_at && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiCalendar size={12} />
                    Rejected: {formatDate(advance.rejected_at)}
                  </div>
                )}
                {advance.status === 'rejected' && advance.rejector && (
                  <div className="flex items-center gap-1 text-red-600">
                    <FiUser size={12} />
                    {getFullName(advance.rejector)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(advance.status)}
              {advance.status === 'requested' && canApproveAdvance && (
                <>
                  <button
                    onClick={() => setShowApprovalDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiCheck className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => setShowRejectDrawer(true)}
                    disabled={actionLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    <FiX className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
              {advance.status === 'approved' && canApproveAdvance && (
                <button
                  onClick={() => setShowTransferDrawer(true)}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <FaMoneyBillWave className="w-4 h-4" />
                  Transfer
                </button>
              )}
              {advance.status === 'transferred' && canApproveAdvance && (
                <button
                  onClick={openCompleteModal}
                  disabled={actionLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <FiCheckCircle className="w-4 h-4" />
                  Complete
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && renderDetailsTab()}
        {activeTab === 'workflow' && renderWorkflowTab()}
        {activeTab === 'logs' && <EntityLogs entity="advance" entityId={id as string} />}


        {/* Approval Drawer */}
        {showApprovalDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => setShowApprovalDrawer(false) : undefined}
            />
            <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Approve Advance</h3>
                  <p className="text-sm text-gray-600 mt-1">Review and approve the requested amount</p>
                </div>
                <button
                  onClick={() => setShowApprovalDrawer(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Approved Amount *
                    </label>
                    <input
                      type="number"
                      value={approvedAmount}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setApprovedAmount(value);
                        setTouched({ ...touched, approvedAmount: true });
                        if (errors.approvedAmount) {
                          setErrors({ ...errors, approvedAmount: '' });
                        }
                      }}
                      max={approvedOrRequestedAmount}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-green-500 ${errors.approvedAmount && touched.approvedAmount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Requested: {formatCurrency(advance?.request_amount)}</p>
                    {errors.approvedAmount && touched.approvedAmount && (
                      <p className="mt-1 text-sm text-red-600">{errors.approvedAmount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Approval Remarks (Optional)
                    </label>
                    <textarea
                      value={approvalRemarks}
                      onChange={(e) => setApprovalRemarks(e.target.value)}
                      rows={3}
                      disabled={actionLoading}
                      placeholder="Add any remarks..."
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiCheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium mb-1">Approval Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Amount can only be equal to or less than requested</li>
                          <li>Verify the advance reason and request details</li>
                          <li>Ensure proper documentation is available</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => setShowApprovalDrawer(false)}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={actionLoading || approvedAmount <= 0}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Approving...' : 'Approve Advance'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Reject Drawer */}
        {showRejectDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => setShowRejectDrawer(false) : undefined}
            />
            <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reject Advance</h3>
                  <p className="text-sm text-gray-600 mt-1">Provide a reason for rejection</p>
                </div>
                <button
                  onClick={() => setShowRejectDrawer(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => {
                        setRejectionReason(e.target.value);
                        setTouched({ ...touched, rejectionReason: true });
                        if (errors.rejectionReason) {
                          setErrors({ ...errors, rejectionReason: '' });
                        }
                      }}
                      rows={4}
                      disabled={actionLoading}
                      placeholder="Please provide a detailed reason for rejection (minimum 10 characters)..."
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-red-500 ${errors.rejectionReason && touched.rejectionReason
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {rejectionReason.length}/500 characters (minimum 10)
                    </p>
                    {errors.rejectionReason && touched.rejectionReason && (
                      <p className="mt-1 text-sm text-red-600">{errors.rejectionReason}</p>
                    )}
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FiAlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-medium mb-1">Rejection Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Provide a clear and specific reason</li>
                          <li>Be professional and constructive</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => setShowRejectDrawer(false)}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={actionLoading || !rejectionReason.trim()}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Rejecting...' : 'Reject Advance'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Transfer Drawer */}
        {showTransferDrawer && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-50"
              onClick={!actionLoading ? () => setShowTransferDrawer(false) : undefined}
            />
            <div className="overflow-y-auto fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Transfer Amount</h3>
                  <p className="text-sm text-gray-600 mt-1">Confirm transfer details</p>
                </div>
                <button
                  onClick={() => setShowTransferDrawer(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>

              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transfer Amount *
                    </label>
                    <input
                      type="number"
                      value={transferData.transferred_amount}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setTransferData({ ...transferData, transferred_amount: value });
                        setTouched({ ...touched, transferred_amount: true });
                        if (errors.transferred_amount) {
                          setErrors({ ...errors, transferred_amount: '' });
                        }
                      }}
                      max={approvedOrRequestedAmount}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.transferred_amount && touched.transferred_amount
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Approved: {formatCurrency(approvedOrRequestedAmount)}</p>
                    {errors.transferred_amount && touched.transferred_amount && (
                      <p className="mt-1 text-sm text-red-600">{errors.transferred_amount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transfer Mode
                    </label>
                    <select
                      value={transferData.transfer_mode}
                      onChange={(e) => setTransferData({ ...transferData, transfer_mode: e.target.value })}
                      disabled={actionLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="upi">UPI</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transfer Date *
                    </label>
                    <input
                      type="date"
                      value={transferData.transfer_date}
                      onChange={(e) => {
                        setTransferData({ ...transferData, transfer_date: e.target.value });
                        setTouched({ ...touched, transfer_date: true });
                        if (errors.transfer_date) {
                          setErrors({ ...errors, transfer_date: '' });
                        }
                      }}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.transfer_date && touched.transfer_date
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    {errors.transfer_date && touched.transfer_date && (
                      <p className="mt-1 text-sm text-red-600">{errors.transfer_date}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transfer Time (24H Format)
                    </label>
                    <TimeInput24h
                      value={transferData.transfer_time}
                      onChange={(time) => {
                        setTransferData({ ...transferData, transfer_time: time });
                        setTouched({ ...touched, transfer_time: true });

                        if (errors.transfer_time) {
                          setErrors({ ...errors, transfer_time: '' });
                        }
                      }}
                      disabled={actionLoading}
                      hasError={errors.transfer_time && touched.transfer_time}
                    />

                    {errors.transfer_time && touched.transfer_time && (
                      <p className="mt-1 text-sm text-red-600">{errors.transfer_time}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={transferData.transfer_reference}
                      onChange={(e) => {
                        setTransferData({ ...transferData, transfer_reference: e.target.value });
                        setTouched({ ...touched, transfer_reference: true });
                        if (errors.transfer_reference) {
                          setErrors({ ...errors, transfer_reference: '' });
                        }
                      }}
                      disabled={actionLoading}
                      placeholder="Transaction ID / Reference"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 ${errors.transfer_reference && touched.transfer_reference
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                        } disabled:bg-gray-50 disabled:cursor-not-allowed`}
                    />
                    {errors.transfer_reference && touched.transfer_reference && (
                      <p className="mt-1 text-sm text-red-600">{errors.transfer_reference}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transfer Remarks (Optional)
                    </label>
                    <textarea
                      value={transferData.transfer_remarks}
                      onChange={(e) => setTransferData({ ...transferData, transfer_remarks: e.target.value })}
                      rows={2}
                      disabled={actionLoading}
                      placeholder="Add any remarks..."
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <div className="flex items-start gap-3">
                      <FaMoneyBillWave className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Transfer Guidelines</p>
                        <ul className="text-xs space-y-1 list-disc list-inside">
                          <li>Amount can only be equal to or less than approved</li>
                          <li>Verify recipient details before transfer</li>
                          <li>Keep the reference number for records</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <button
                      onClick={() => setShowTransferDrawer(false)}
                      disabled={actionLoading}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleTransfer}
                      disabled={
                        actionLoading ||
                        transferData.transferred_amount < 0 ||
                        !transferData.transfer_date
                      }
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading ? 'Transferring...' : 'Complete Transfer'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Complete Advance Modal */}
        {showCompleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md bg-white rounded shadow-xl">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Complete Advance</h3>
                  <p className="text-sm text-gray-600 mt-1">Add the reference details of the transfer</p>
                </div>
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="p-2 hover:bg-gray-200 rounded transition-colors"
                  disabled={actionLoading}
                >
                  <FiX size={20} className="text-gray-600" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number *</label>
                  <input
                    type="text"
                    value={completeData.transfer_reference}
                    maxLength={255}
                    onChange={(e) => setCompleteData({ ...completeData, transfer_reference: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter reference number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (Optional)</label>
                  <textarea
                    value={completeData.completion_remarks}
                    rows={3}
                    onChange={(e) => setCompleteData({ ...completeData, completion_remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    placeholder="Add remarks..."
                  />
                </div>
                {completeError && (
                  <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{completeError}</div>
                )}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowCompleteModal(false)}
                    disabled={actionLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleComplete}
                    disabled={actionLoading || !completeData.transfer_reference.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded hover:bg-teal-700 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? 'Completing...' : 'Mark Complete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceView;
